// src/pages/compliance/PrivacyPolicy.tsx
import React, { useEffect } from "react";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import { Shield, Eye, Lock, UserX, FileText, Mail, AlertTriangle, Globe } from "lucide-react";
import Header from "../Home/Header";
import Footer from "../Home/Footer";

const PrivacyPolicy: React.FC = () => {
  useEffect(() => {
    document.title = "Privacy Policy - BidBro";
    window.scrollTo(0, 0);
  }, []);

  const sections = [
    {
      icon: Eye,
      title: "Information We Collect",
      content: [
        {
          subtitle: "Personal Information",
          items: [
            "Name, email address, phone number, and mailing address",
            "Account credentials and profile information",
            "Payment information (processed securely through third-party providers)",
            "Professional licenses and certifications (for contractors)",
            "Government-issued identification for verification purposes"
          ]
        },
        {
          subtitle: "Project Information",
          items: [
            "Project descriptions, specifications, and requirements",
            "Photos, videos, and documents you upload",
            "Location information for project sites",
            "Communication records between users and contractors",
            "Bid submissions and project history"
          ]
        },
        {
          subtitle: "Automatically Collected Information",
          items: [
            "IP address, browser type, and device information",
            "Usage patterns, page views, and time spent on our platform",
            "Cookies and similar tracking technologies",
            "Location data (with your permission)",
            "Search queries and interaction history"
          ]
        }
      ]
    },
    {
      icon: Shield,
      title: "How We Use Your Information",
      content: [
        {
          subtitle: "Platform Operations",
          items: [
            "Facilitate connections between homeowners and contractors",
            "Process and manage project bids and communications",
            "Verify contractor credentials and background checks",
            "Provide customer support and resolve disputes",
            "Maintain and improve our platform security"
          ]
        },
        {
          subtitle: "Communication",
          items: [
            "Send project updates, notifications, and alerts",
            "Provide customer support and respond to inquiries",
            "Share marketing communications (with your consent)",
            "Send important service announcements and policy updates",
            "Enable messaging between platform users"
          ]
        },
        {
          subtitle: "Analytics and Improvement",
          items: [
            "Analyze usage patterns to improve our services",
            "Conduct research and analytics for business insights",
            "Personalize your experience and recommendations",
            "Detect and prevent fraud or security breaches",
            "Comply with legal obligations and law enforcement requests"
          ]
        }
      ]
    },
    {
      icon: Globe,
      title: "Information Sharing",
      content: [
        {
          subtitle: "With Other Users",
          items: [
            "Your profile information is visible to potential project partners",
            "Project details are shared with matched contractors",
            "Reviews and ratings are publicly displayed",
            "Communication history is accessible to involved parties",
            "Location information is shared for project matching"
          ]
        },
        {
          subtitle: "With Service Providers",
          items: [
            "Payment processors for transaction handling",
            "Background check companies for contractor verification",
            "Cloud storage providers for data hosting",
            "Email and communication service providers",
            "Analytics and marketing platforms (with anonymized data)"
          ]
        },
        {
          subtitle: "Legal Disclosure",
          items: [
            "When required by law or court order",
            "To protect our rights, property, or safety",
            "In connection with business transfers or mergers",
            "To prevent fraud or investigate security incidents",
            "With your explicit consent for specific purposes"
          ]
        }
      ]
    },
    {
      icon: Lock,
      title: "Data Security",
      content: [
        {
          subtitle: "Security Measures",
          items: [
            "Industry-standard encryption for data transmission and storage",
            "Secure servers with restricted access controls",
            "Regular security audits and vulnerability assessments",
            "Multi-factor authentication for sensitive accounts",
            "Secure payment processing through PCI-compliant providers"
          ]
        },
        {
          subtitle: "Data Retention",
          items: [
            "Account information retained while account is active",
            "Project data retained for 7 years for legal compliance",
            "Communication records retained for dispute resolution",
            "Payment information retained as required by financial regulations",
            "Analytics data may be retained indefinitely in anonymized form"
          ]
        }
      ]
    },
    {
      icon: UserX,
      title: "Your Privacy Rights",
      content: [
        {
          subtitle: "Access and Control",
          items: [
            "Access and download your personal information",
            "Correct or update your account information",
            "Delete your account and associated data",
            "Opt-out of marketing communications",
            "Request data portability to another service"
          ]
        },
        {
          subtitle: "Privacy Settings",
          items: [
            "Control visibility of your profile information",
            "Manage communication preferences and notifications",
            "Choose cookie and tracking preferences",
            "Limit data sharing with third parties",
            "Set location sharing preferences"
          ]
        },
        {
          subtitle: "State-Specific Rights",
          items: [
            "California residents have additional rights under CCPA",
            "EU residents have rights under GDPR",
            "Virginia residents have rights under VCDPA",
            "Other state laws may provide additional protections",
            "Contact us to exercise your specific rights"
          ]
        }
      ]
    },
    {
      icon: FileText,
      title: "Cookies and Tracking",
      content: [
        {
          subtitle: "Types of Cookies",
          items: [
            "Essential cookies for platform functionality",
            "Performance cookies for analytics and optimization",
            "Functional cookies for personalized features",
            "Advertising cookies for relevant marketing (with consent)",
            "Third-party cookies from integrated services"
          ]
        },
        {
          subtitle: "Cookie Management",
          items: [
            "You can control cookies through browser settings",
            "Opt-out of non-essential cookies through our cookie banner",
            "Some features may not work if cookies are disabled",
            "Third-party cookies are governed by their respective policies",
            "Clear cookies periodically for enhanced privacy"
          ]
        }
      ]
    },
    {
      icon: AlertTriangle,
      title: "Important Notices",
      content: [
        {
          subtitle: "Children's Privacy",
          items: [
            "Our platform is not intended for users under 18",
            "We do not knowingly collect information from minors",
            "Parents should monitor their children's online activities",
            "Contact us immediately if you believe a minor has created an account",
            "We will promptly delete any information from underage users"
          ]
        },
        {
          subtitle: "International Users",
          items: [
            "Your data may be processed in the United States",
            "We comply with applicable international data protection laws",
            "Data transfers are protected by appropriate safeguards",
            "EU users have specific rights under GDPR",
            "Contact us for information about data transfer mechanisms"
          ]
        },
        {
          subtitle: "Policy Updates",
          items: [
            "We may update this policy to reflect changes in our practices",
            "Material changes will be communicated via email or platform notice",
            "Continued use constitutes acceptance of updated terms",
            "Previous versions are available upon request",
            "Check this page regularly for the most current version"
          ]
        }
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header isFullWidth={true} />
      
      <main style={{marginTop: '75px', paddingTop: '50px'}}>
        <Container>
          <Row className="justify-content-center">
            <Col lg={10} xl={8}>
              {/* Header Section */}
              <div className="text-center mb-5 mt-10">
                <div className="d-flex align-items-center justify-content-center mb-3">
                  <Shield className="text-primary me-2" size={32} />
                  <h1 className="display-4 fw-bold mb-0">Privacy Policy</h1>
                </div>
                <p className="text-muted fs-5 mb-4">
                  Your privacy is important to us. This policy explains how we collect, use, and protect your information.
                </p>
                <div className="bg-light rounded p-3 mb-4">
                  <p className="mb-2">
                    <strong>Last Updated:</strong> {new Date().toLocaleDateString('en-US', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })}
                  </p>
                  <p className="mb-0 text-muted">
                    <strong>Effective Date:</strong> This policy is effective immediately for new users and will be effective 30 days after posting for existing users.
                  </p>
                </div>
              </div>

              {/* Introduction */}
              <Card className="shadow-sm mb-5">
                <CardBody className="p-4">
                  <h2 className="h4 fw-bold mb-3">Introduction</h2>
                  <p className="text-muted mb-3">
                    BidBro ("we," "our," or "us") operates a platform that connects homeowners with contractors for home improvement projects. 
                    We are committed to protecting your privacy and being transparent about how we collect, use, and share your information.
                  </p>
                  <p className="text-muted mb-3">
                    This Privacy Policy applies to all users of our website, mobile applications, and related services (collectively, the "Platform"). 
                    By using our Platform, you consent to the practices described in this policy.
                  </p>
                  <div className="alert alert-info">
                    <strong>Contact Information:</strong> If you have questions about this policy, please contact us at 
                    <a href="mailto:privacy@bidbro.com" className="text-decoration-none ms-1">privacy@bidbro.com</a> or 
                    <a href="mailto:support@bidbro.com" className="text-decoration-none ms-1">support@bidbro.com</a>.
                  </div>
                </CardBody>
              </Card>

              {/* Policy Sections */}
              {sections.map((section, index) => (
                <Card key={index} className="shadow-sm mb-4">
                  <CardBody className="p-4">
                    <div className="d-flex align-items-center mb-4">
                      <section.icon className="text-primary me-3" size={24} />
                      <h2 className="h4 fw-bold mb-0">{section.title}</h2>
                    </div>
                    
                    {section.content.map((subsection, subIndex) => (
                      <div key={subIndex} className="mb-4">
                        <h3 className="h5 fw-semibold mb-3 text-primary">{subsection.subtitle}</h3>
                        <ul className="list-unstyled">
                          {subsection.items.map((item, itemIndex) => (
                            <li key={itemIndex} className="mb-2 d-flex">
                              <span className="text-primary me-2">•</span>
                              <span className="text-muted">{item}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </CardBody>
                </Card>
              ))}

              {/* Contact and Legal Information */}
              <Card className="shadow-sm mb-5">
                <CardBody className="p-4">
                  <div className="d-flex align-items-center mb-4">
                    <Mail className="text-primary me-3" size={24} />
                    <h2 className="h4 fw-bold mb-0">Contact Us</h2>
                  </div>
                  
                  <Row>
                    <Col md={6}>
                      <h3 className="h5 fw-semibold mb-3">Privacy Questions</h3>
                      <p className="text-muted mb-2">
                        <strong>Email:</strong> <a href="mailto:privacy@bidbro.com" className="text-decoration-none">privacy@bidbro.com</a>
                      </p>
                      <p className="text-muted mb-2">
                        <strong>General Support:</strong> <a href="mailto:support@bidbro.com" className="text-decoration-none">support@bidbro.com</a>
                      </p>
                      <p className="text-muted mb-4">
                        <strong>Phone:</strong> (757) 263-0869
                      </p>
                    </Col>
                    <Col md={6}>
                      <h3 className="h5 fw-semibold mb-3">Mailing Address</h3>
                      <p className="text-muted mb-0">
                        BidBro, Inc<br />
                        16192 Coastal Highway<br />
                        Lewes, DE 19958
                      </p>
                    </Col>
                  </Row>
                  
                  <hr className="my-4" />
                  
                  <div className="alert alert-warning">
                    <h4 className="alert-heading h5">Data Subject Requests</h4>
                    <p className="mb-2">
                      To exercise your privacy rights (access, deletion, correction, etc.), please contact us using the information above. 
                      We will respond to verified requests within the timeframes required by applicable law.
                    </p>
                    <p className="mb-0">
                      For security purposes, we may need to verify your identity before processing certain requests.
                    </p>
                  </div>
                </CardBody>
              </Card>

              {/* Legal Disclaimer */}
              <div className="text-center text-muted small mb-5">
                <p className="mb-2">
                  This privacy policy constitutes a legal agreement between you and BidBro, Inc. 
                  By using our platform, you acknowledge that you have read, understood, and agree to be bound by this policy.
                </p>
                <p className="mb-0">
                  For questions about our Terms of Service, please visit our <a href="/terms" className="text-decoration-none">Terms of Service page</a>.
                </p>
              </div>
            </Col>
          </Row>
        </Container>
      </main>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;